import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Card from '@/components/card';
import Title from '@/components/title';
import {
  ISalesReportSumByMonthReq,
  ISalesReportSumByMonthRes,
} from '@/interfaces/report';
import dayjs, { Dayjs } from 'dayjs';
import {
  DatePicker,
  DatePickerProps,
  Select,
  Table,
  TableColumnsType,
} from 'antd';
import locale from 'antd/es/date-picker/locale/th_TH';
import { ReportMenu } from '@/configs/menuItems';
import { getPathSegment } from '@/utils/pathUtils';
import SubMenu from '@/components/menu/sub-menu';
import { AxiosResponse } from 'axios';
import { searchSalesReportByMonth } from '@/adapters/report';
import { formatNumber } from '@/utils/dataUtils';
import BusinessUnitSelector from '@/components/selector/business-unit-selector';
import CustomerSelector from '@/components/selector/customer-selector';
import { ICustomer } from '@/interfaces/customer';
import ProductSelector from '@/components/selector/product-selector';
import { IProduct } from '@/interfaces/product';
import ExcelExport from './components/export-report';
import { useAuth } from '@/contexts/AuthContext';

const SalesReport = () => {
  const pathSegment = getPathSegment();
  const { userInfo } = useAuth();
  const [criteria, setCriteria] = useState<ISalesReportSumByMonthReq>({
    startDate: dayjs().startOf('year').format('YYYY-MM-DD'),
    endDate: dayjs().add(1, 'year').endOf('year').format('YYYY-MM-DD'),
    businessUnitId: userInfo?.bu || undefined,
  });

  const { data, isLoading } = useQuery<
    AxiosResponse<ISalesReportSumByMonthRes[]>,
    Error
  >({
    queryKey: ['search-sales-report', criteria],
    queryFn: async () => await searchSalesReportByMonth(criteria),
  });

  const onRangeChange = (dates: null | (Dayjs | null)) => {
    if (dates) {
      const startDate = dayjs(dates).startOf('year').format('YYYY-MM-DD');
      const endDate = dayjs(dates)
        .add(1, 'year')
        .endOf('year')
        .format('YYYY-MM-DD');
      setCriteria({
        ...criteria,
        startDate: startDate,
        endDate: endDate,
      });
    } else {
      setCriteria({
        ...criteria,
      });
    }
  };

  const columns: TableColumnsType<ISalesReportSumByMonthRes> = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      width: 50,
      align: 'center',
      fixed: 'left',
      className: 'text-center',
      render: (_, rec, i) => <div>{i + 1}</div>,
    },
    {
      title: 'BU',
      dataIndex: 'businessUnitName',
      key: 'businessUnitName',
      width: 100,
      align: 'center',
      className: 'font-bold',
      fixed: 'left',
    },
    {
      title: 'CustomerName',
      dataIndex: 'customerName',
      key: 'customerName',
      width: 300,
      render: _ => <div className="truncate">{_}</div>,
    },
    {
      title: 'Ship to',
      dataIndex: 'shippingAddressName',
      key: 'shippingAddressName',
      width: 220,
      fixed: 'left',
      render: _ => <div className="truncate">{_}</div>,
    },
    {
      title: 'Item Code',
      dataIndex: 'productCode',
      key: 'productCode',
      width: 130,
      align: 'center',
    },
    {
      title: 'Item Name',
      dataIndex: 'productName',
      key: 'productName',
      width: 250,
      fixed: 'left',
      render: _ => <div className="truncate">{_}</div>,
    },
    {
      title: `${dayjs(criteria.startDate).format('YYYY')}`,
      children: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(n => {
        return {
          title: dayjs()
            .month(n - 1)
            .format('MMM'),
          dataIndex: `mQ${n}`,
          key: `mQ${n}`,
          width: 100,
          align: 'right',
          render: _ => (
            <div className="font-bold">{(_ && formatNumber(_)) || '-'}</div>
          ),
        };
      }),
    },
    {
      title: `${dayjs(criteria.endDate).format('YYYY')}`,
      children: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(n => {
        return {
          title: dayjs()
            .month(n - 1)
            .format('MMM'),
          dataIndex: `mQ${12 + n}`,
          key: `mQ${12 + n}`,
          width: 100,
          align: 'right',
          render: _ => (
            <div className="font-bold">{(_ && formatNumber(_)) || '-'}</div>
          ),
        };
      }),
    },
  ];

  const getYearMonth = (date: Dayjs) => date.year() * 12 + date.month();

  const disabled24MonthsDate: DatePickerProps['disabledDate'] = (
    current,
    { from, type },
  ) => {
    if (from) {
      const minDate = from.add(-11, 'months');
      const maxDate = from.add(11, 'months');

      switch (type) {
        case 'year':
          return (
            current.year() < minDate.year() || current.year() > maxDate.year()
          );

        default:
          return (
            getYearMonth(current) < getYearMonth(minDate) ||
            getYearMonth(current) > getYearMonth(maxDate)
          );
      }
    }

    return false;
  };

  return (
    <>
      <SubMenu active={pathSegment} items={ReportMenu} />
      <Card customClass="min-h-[200px]">
        <Title title="Sales Report">
          <ExcelExport
            data={data?.data || []}
            startDate={dayjs(criteria?.startDate).format('YYYY')}
            endDate={dayjs(criteria?.endDate).format('YYYY')}
          />
        </Title>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col md:flex-row justify-between gap-3">
            <div className="flex flex-col md:flex-row justify-between gap-3">
              <Select
                className="md:min-w-[150px] w-full"
                size={'large'}
                placeholder="เลือกประเภท"
                onChange={(v: 'KG' | 'SALES') => {
                  setCriteria({ ...criteria, reportType: v ?? 'KG' });
                }}
                defaultValue={'KG'}
                allowClear={false}
                options={[
                  {
                    label: 'KG',
                    value: 'KG',
                  },
                  {
                    label: 'Sales',
                    value: 'SALES',
                  },
                ]}
              />
              <BusinessUnitSelector
                handleSelectData={function (id: number): void {
                  setCriteria({ ...criteria, businessUnitId: id });
                }}
                disabled={!!userInfo?.bu}
                value={criteria.businessUnitId}
              />
              <CustomerSelector
                handleSelectData={function (
                  customer: ICustomer | undefined,
                ): void {
                  setCriteria({ ...criteria, customerId: customer?.id });
                }}
                customClass="md:min-w-[300px]"
              />
              <ProductSelector
                customClass="md:min-w-[300px]"
                handleSelectData={function (item: IProduct): void {
                  setCriteria({
                    ...criteria,
                    productId: item?.id,
                  });
                }}
              />
              <DatePicker
                picker="year"
                size="large"
                locale={locale}
                defaultValue={dayjs(criteria.startDate)}
                onChange={onRangeChange}
                disabledDate={disabled24MonthsDate}
                allowClear={false}
                className="min-w-[150px]"
                format={`YYYY - ${dayjs(criteria.startDate).add(1, 'year').format('YYYY')}`}
              />
            </div>
          </div>
          <Table<ISalesReportSumByMonthRes>
            columns={columns}
            dataSource={data?.data || []}
            bordered
            size="middle"
            scroll={{ x: 'calc(1400px + 50%)', y: 50 * 10 }}
            loading={isLoading}
          />
        </div>
      </Card>
    </>
  );
};

export default SalesReport;
